import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { helpArticleAvailable, type HelpCustomArticleSummary } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { usePermissions } from '@/lib/permissions';
import { useUi } from '@/lib/store';
import { canRead, canShowAll } from '@/help/access';
import { areaById, HELP_AREAS } from '@/help/areas';
import type { HelpMeta } from '@/help/types';

export const helpKeys = {
  manifest: ['help', 'manifest'] as const,
  body: (key: string) => ['help', 'body', key] as const,
  custom: (ws: string, all: boolean) => ['help', ws, 'custom', all] as const,
  customOne: (ws: string, id: string) => ['help', ws, 'custom', 'one', id] as const,
  feedback: (ws: string, key: string) => ['help', ws, 'feedback', key] as const,
  report: (ws: string) => ['help', ws, 'report'] as const,
};

/** The guide's metadata (a separate chunk, loaded on first use and cached for the session). */
export function useHelpManifest() {
  return useQuery({ queryKey: helpKeys.manifest, queryFn: async () => (await import('virtual:help-manifest')).articles, staleTime: Infinity, gcTime: Infinity });
}

/** One article's HTML (bodies are split into a chunk per area). */
export function useHelpBody(key: string | null) {
  return useQuery({
    queryKey: helpKeys.body(key ?? ''), enabled: Boolean(key), staleTime: Infinity,
    queryFn: async () => {
      const [area, slug] = key!.split('/');
      const { loaders } = await import('virtual:help-manifest');
      const load = loaders[area];
      if (!load) return null;
      return (await load()).default[slug] ?? null;
    },
  });
}

/** Admins can switch on "Show all guides"; everyone else always sees what their permissions allow. */
export function useHelpAccess() {
  const mine = usePermissions().data;
  const showAllPref = useUi(s => s.helpShowAll);
  const admin = canShowAll(mine);
  const showAll = admin && showAllPref;
  // t369u: guides for a switched-off module are hidden from everyone (Show all guides too).
  const on = useCallback((a: Partial<Pick<HelpMeta, 'area' | 'routes'>>) => !a.area || helpArticleAvailable(mine?.modules_off, { area: a.area, routes: a.routes }), [mine]);
  const readable = useCallback((a: Pick<HelpMeta, 'permissions'> & Partial<Pick<HelpMeta, 'area' | 'routes'>>) => on(a) && (showAll || canRead(mine, a)), [mine, showAll, on]);
  const forMe = useCallback((a: Pick<HelpMeta, 'permissions'> & Partial<Pick<HelpMeta, 'area' | 'routes'>>) => on(a) && canRead(mine, a), [mine, on]);
  return { mine, admin, showAll, readable, forMe };
}

/** Guide articles this person can read, sorted by area then order. */
export function useVisibleGuides() {
  const m = useHelpManifest();
  const { readable } = useHelpAccess();
  const order = useMemo(() => new Map(HELP_AREAS.map((a, i) => [a.id, i])), []);
  const list = useMemo(() => (m.data ?? []).filter(readable)
    .sort((a, b) => (order.get(a.area) ?? 99) - (order.get(b.area) ?? 99) || a.order - b.order || a.title.localeCompare(b.title)), [m.data, readable, order]);
  return { ...m, data: list, all: m.data ?? [] };
}

/** Workspace-specific articles (company procedures). `all` (help managers) includes drafts and restricted ones. */
export function useCustomArticles(all = false) {
  const ws = useWorkspaceId();
  const { admin, showAll } = useHelpAccess();
  const wantAll = all || (admin && showAll);
  return useQuery({ queryKey: helpKeys.custom(ws ?? '', wantAll), queryFn: () => api.helpArticles(ws!, wantAll), enabled: Boolean(ws), staleTime: 60_000 });
}

export const areaTitle = (id: string) => areaById(id)?.title ?? id;
export const customKey = (a: Pick<HelpCustomArticleSummary, 'id'>) => `company/${a.id}`;
export const helpHref = (key: string, anchor?: string | null) => `/help/${key}${anchor ? `#${anchor}` : ''}`;

export const AUDIENCE_LABEL: Record<HelpMeta['audience'], string> = { everyone: 'Everyone', managers: 'Managers', admins: 'Admins' };
